import {
  URL_ADAF,
  URL_ADDRESS_PAGE,
  URL_USER_OFFERS,
  URL_USER_ORGANIZATION_TEAM,
  URL_SUBSCRIPTION,
  URL_USER_NOTIFICATIONS,
  URL_USER_PROFILE_MANAGE,
  URL_CONTACT,
} from '@tools/routes/routes';
import { defineStore, createPinia } from 'pinia';
import { ref, computed } from 'vue';
import type { UserNotification } from '@models/notification.ts';
import { useModal } from '@composables/useModal.ts';
import type { HelpMenuLinks, HelpMenuSubmenu } from '@global-types/nav-v2/helpMenuItem';
import type { Url } from '@global-types/url';

export const useNavbarStore = defineStore('navbar', () => {
  const notifications = ref<UserNotification>(window.USER_NOTIFICATIONS as UserNotification);
  const helpMenuData = ref<HelpMenuLinks>(window.NEW_HELP_MENU);

  const urlExpanded: string[] = [
    URL_USER_NOTIFICATIONS,
    URL_USER_PROFILE_MANAGE,
    URL_USER_OFFERS,
    URL_SUBSCRIPTION,
    URL_ADAF,
    URL_ADDRESS_PAGE,
    URL_USER_ORGANIZATION_TEAM,
  ] as const;

  const urlExpandedPatterns: RegExp[] = [
    /^\/spaces\/\d+$/,
    /^\/organization\/business_input\/\d{6}$/,
  ] as const;

  const isExpanded = ref<boolean>(false);
  const currentDropdownOpen = ref<string | null>(null);

  const setCurrentDropdownOpen = (newCurrentDropdownOpen: string | null): void => {
    currentDropdownOpen.value = newCurrentDropdownOpen;
  };

  const toggleExpanded = (): void => {
    isExpanded.value = !isExpanded.value;

    setCurrentDropdownOpen(null);
  };

  const isCurrentPage = (link: string | undefined): boolean => window.location.pathname === link;

  const {
    isOpen: isPlusMenuOpen,
    toggleModal: togglePlusMenu,
    closeModal: closePlusMenu,
  } = useModal();

  const isCurrentDropdownOpen = (dropdown: string): boolean => currentDropdownOpen.value === dropdown;

  const setExpanded = (expanded: boolean): void => {
    isExpanded.value = expanded;
  };

  const initExpanded = (): void => {
    const { pathname } = window.location;

    const isExactMatch = urlExpanded.includes(pathname);
    const isPatternMatch = urlExpandedPatterns.some((pattern) => pattern.test(pathname));

    if (isExactMatch || isPatternMatch) {
      setExpanded(true);
    }
  };

  initExpanded();

  const whatsappLink = computed<Url | null>(() => helpMenuData.value.whatsappLink);
  const recalledSubmenus = computed<HelpMenuSubmenu[]>(() => helpMenuData.value.recalledSubmenus);
  const faqLink = computed<Url | null>(() => helpMenuData.value.intercomFaqLink);
  const recallLink = computed<Url | null>(() => helpMenuData.value.recallLink);
  const salesPhone = computed<string | null>(() => helpMenuData.value.salesPhone);

  return {
    isExpanded,
    setExpanded,
    toggleExpanded,
    isCurrentPage,
    notifications,
    isPlusMenuOpen,
    togglePlusMenu,
    closePlusMenu,
    currentDropdownOpen,
    setCurrentDropdownOpen,
    isCurrentDropdownOpen,
    whatsappLink,
    recalledSubmenus,
    faqLink,
    recallLink,
    salesPhone,
  };
});

export const piniaNavbarStore = createPinia();
